import Image from "components/imageNew/image";
import { getHumanFriendlyNumber } from "lib/utils";
import { LockProIcon } from "components/icons/icons";
import { isRevenueField } from "content-ui/leaderboards/lib";

import type {
  LeaderboardColumn,
  LeaderboardCompany,
} from "lib/types/leaderboards";

type CellProps = {
  column: LeaderboardColumn | "foundedYear" | "latestFundraise";
  company: LeaderboardCompany;
  isProUser?: boolean;
};

export const LockedValue = () => (
  <div
    className={"flex items-center gap-[6px] leading-[130%] text-pro-blue"}
    data-testid={"locked-cell"}
  >
    <LockProIcon className={"h-[18px] w-[12px] shrink-0"} />
    <span className={"font-bold text-[16px]"}>Pro</span>
  </div>
);

export const formatMoneyToMillions = (num: number | undefined): string => {
  if (!num) {
    return "-";
  }

  if (num < 100000) {
    return `< €0.1m`;
  }

  const mils = num / 1000000;

  if (mils % 1) {
    return `€${mils.toFixed(1)}m`;
  }

  return `€${mils}m`;
};

const Cell = ({ column, company, isProUser = true }: CellProps) => {
  if (!isProUser && isRevenueField(column)) {
    return <LockedValue />;
  }

  if (column === "rank") {
    let diff: number | undefined = undefined;

    if (company.previousRank) {
      diff = company.previousRank - company.rank;
    }

    return (
      <div className={"flex flex-col gap-[6px]"}>
        <span
          className={"font-bold text-[18px] leading-[130%] text-black-rock"}
        >
          {company.rank}
        </span>
        <span className={"text-[14px] leading-[130%]"}>
          {diff === undefined && <span>New</span>}
          {diff === 0 && <span className={"text-mono-20"}>( - )</span>}
          {diff !== undefined && diff > 0 && (
            <span className={"text-[#00C087]"}>+{diff}</span>
          )}
          {diff !== undefined && diff < 0 && (
            <span className={"text-[#E52900]"}>{diff}</span>
          )}
        </span>
      </div>
    );
  }

  if (column === "companyInfo") {
    return (
      <div className={"flex gap-[8px] leading-[130%]"}>
        {company.logo && (
          <Image
            className={"h-[50px] w-[50px]"}
            src={company.logo}
            alt={`${company.companyName} logo`}
            sizes="50px"
          />
        )}
        <div className={"flex flex-col gap-[8px] text-[16px]"}>
          <h4 className={"font-bold"}>{company.companyName}</h4>
          <p className={"text-mono-40"}>{company.sector}</p>
        </div>
      </div>
    );
  }

  if (column === "employees" && company.employees) {
    return (
      <div className={"flex leading-[130%]"}>
        <p className={"text-[16px]"}>
          {getHumanFriendlyNumber(company.employees)}
        </p>
      </div>
    );
  }

  if (column === "totalFunding" && company.totalFunding) {
    return (
      <div>
        <p>{formatMoneyToMillions(company.totalFunding)}</p>
      </div>
    );
  }

  if (column === "latestRevenue" && company.latestRevenue) {
    return (
      <div className={"leading-[130%]"}>
        <p>{formatMoneyToMillions(company.latestRevenue)}</p>
      </div>
    );
  }

  if (column === "revenueCagr" && company.revenueCagr) {
    return (
      <div className={"leading-[130%]"}>
        <p>{company.revenueCagr}%</p>
      </div>
    );
  }

  if (column === "latestFundraise") {
    return (
      <div className={"flex leading-[130%]"}>
        {company.latestFundraise ? (
          <p>
            {formatMoneyToMillions(company.latestFundraise)}
            {company.latestFundraiseDate && (
              <>
                {" "}
                / <br /> {company.latestFundraiseDate}
              </>
            )}
          </p>
        ) : (
          <p>-</p>
        )}
      </div>
    );
  }

  if (company[column]) {
    return (
      <div className={"flex leading-[130%]"}>
        <p>{company[column]}</p>
      </div>
    );
  }

  return (
    <div className={"flex leading-[130%]"}>
      <p>-</p>
    </div>
  );
};

export default Cell;
